import { debounce } from 'lodash';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useMemo, useState, useEffect, useCallback } from 'react';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { LoadingButton } from '@mui/lab';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';
import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import {
  mergeHighlights,
  allHighlightTerms,
  keywordHighlights,
  columnSearchHighlights,
  serializeHighlightTerms,
} from 'src/utils/highlight-terms';

import { useTranslate } from 'src/locales';
import { useAppDispatch } from 'src/store/store';
import { SortOrder } from 'src/services/common.types';
import { ListEntityType } from 'src/services/lists/lists.types';
import { SortConfig, PageOptions } from 'src/services/api.types';
import { IOrgCandidateListItem } from 'src/services/candidates/candidates.types';
import { openMultiMailerDialog, openSingleMailerDialog } from 'src/store/slices/app/appSlice';
import { MailerTargetType, EmailMessageTemplateType } from 'src/services/email-sync/email-sync.types';
import { toggleCandidateInviteModal, toggleCandidateEditorSidesheet, toggleCandidateFileImporterModal } from 'src/store/slices/candidate/candidateSlice';
import {
  exportOrgCandidateProfile,
  generateRemoveCandidateErrorMessage,
  generateUnlinkCandidateErrorMessage,
  generateDirectCandidateInviteMessage,
} from 'src/services/candidates/candidates.utils';
import {
  useRemoveCandidateMutation,
  useUnlinkCandidateMutation,
  useGetMyCandidatesLeanQuery,
  useCreateDirectInviteMutation,
  useUpdateCandidateRatingMutation,
} from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import DataList from 'src/components/data-list/data-list';
import { ConfirmDialog } from 'src/components/custom-dialog';
import RecordPanel from 'src/components/record-panel/record-panel';
import { useRecordPanel } from 'src/components/record-panel/use-record-panel';
import DataListActionsMenu from 'src/components/data-list/data-list-actions-menu';
import DataListColumnPicker from 'src/components/data-list/data-list-column-picker';
import { useColumnPreferences } from 'src/components/data-list/use-column-preferences';

import AddItemsModal from 'src/sections/lists/modals/add-items-modal';
import AddCandidateJobModal from 'src/sections/jobs/modals/add-candidate-job-modal';
import BulkAddCandidatesJobModal from 'src/sections/jobs/modals/bulk-add-candidate-job-modal';
import BulkCandidateImportModal from 'src/sections/candidates/modal/bulk-candidate-import-modal';

import CandidateRowActions from './candidate-row-actions';
import { buildCandidateColumns } from './candidate-columns';

const PAGE_SIZE = 50;

/**
 * Candidate list on the shared DataList (JT1-1527/1528). Replaces the old candidate-table:
 * configurable/sortable columns from the lean endpoint, infinite scroll, and every action the old
 * list had (rating, add-to-job, invite, mailer, call/SMS, unlink, remove, export, bulk add/list/mail,
 * create/import). The old availability/skills/labels toolbar filters are gone — superseded by
 * per-column search (item 3, follow-up) — leaving a global search box.
 */
export default function CandidatesList() {
  const { t } = useTranslate();
  const dispatch = useAppDispatch();
  const { openRecord } = useRecordPanel();
  const authContext = useAuthContext();
  const tenant = useOrgTenant();

  // Query state
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<IOrgCandidateListItem[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [columnSearch, setColumnSearch] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<SortConfig>({ sort_by: 'last_activity_at', sort_order: SortOrder.DESC });
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [currentCandidate, setCurrentCandidate] = useState<string | undefined>();

  const pageOptions: PageOptions = useMemo(() => ({ page, per_page: PAGE_SIZE }), [page]);

  const { currentData, isFetching } = useGetMyCandidatesLeanQuery(
    { search_query: filterQuery || undefined, column_filters: columnSearch, page: pageOptions, sortConfig },
    { skip: !authContext.organization?.id, refetchOnMountOrArgChange: true }
  );

  // Accumulate pages for infinite scroll: page 1 replaces, later pages append (deduped by id so an
  // overlapping refetch can't double a row).
  useEffect(() => {
    if (!currentData) return;
    setRows((prev) => {
      if (page === 1) return currentData.results;
      const seen = new Set(prev.map((r) => r.id));
      return [...prev, ...currentData.results.filter((r) => !seen.has(r.id))];
    });
  }, [currentData, page]);

  const count = currentData?.count ?? 0;
  const hasMore = rows.length < count;
  // searchInput !== filterQuery means a keystroke hasn't been sent yet — keep the searching state on.
  const isSearching = isFetching || searchInput !== filterQuery;

  const resetToFirstPage = useCallback(() => setPage(1), []);

  const applySearch = useMemo(
    () => debounce((value: string) => { setFilterQuery(value); resetToFirstPage(); }, 300),
    [resetToFirstPage]
  );
  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const onSearchChange = useCallback((value: string) => { setSearchInput(value); applySearch(value); }, [applySearch]);

  const onSortChange = useCallback((next: SortConfig) => { setSortConfig(next); resetToFirstPage(); }, [resetToFirstPage]);

  // Per-column search: a blank value drops the key so it's omitted from the request.
  const onColumnSearchChange = useCallback((key: string, value: string) => {
    setColumnSearch((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    resetToFirstPage();
  }, [resetToFirstPage]);

  const onLoadMore = useCallback(() => { if (!isFetching && hasMore) setPage((p) => p + 1); }, [isFetching, hasMore]);

  // Selection
  const onSelectRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);
  const onSelectAll = useCallback((checked: boolean, ids: string[]) => {
    setSelectedIds(checked ? Array.from(new Set(ids)) : []);
  }, []);

  // Modals / confirms
  const addToJob = useBoolean();
  const bulkAddToJob = useBoolean();
  const addToList = useBoolean();
  const bulkImport = useBoolean();
  const unlinkConfirm = useBoolean();
  const removeConfirm = useBoolean();

  const [updateRating] = useUpdateCandidateRatingMutation();
  const [createDirectInvite] = useCreateDirectInviteMutation();
  const [unlinkCandidate, { isLoading: isUnlinking }] = useUnlinkCandidateMutation();
  const [removeCandidate, { isLoading: isRemoving }] = useRemoveCandidateMutation();

  // What the rows were searched for — the keyword box and the column searches, as sent — highlighted in
  // the cells and carried onto opened records as `?q=` (JT1-1693). An empty search clears `q`.
  const highlights = useMemo(
    () => mergeHighlights(keywordHighlights(filterQuery), columnSearchHighlights(columnSearch)),
    [filterQuery, columnSearch]
  );
  const highlightQuery = useMemo(() => serializeHighlightTerms(allHighlightTerms(highlights)) || undefined, [highlights]);

  // Row actions open the context-preserving record panel (JT1-1529), not a hard navigation. The eye
  // (and the kebab "view") open the right-side quick view; the name opens the centred overlay.
  const handleQuickView = useCallback((id: string) => openRecord('candidate', id, { mode: 'quick', query: { q: highlightQuery } }), [openRecord, highlightQuery]);
  const handleOpenOverlay = useCallback((id: string) => openRecord('candidate', id, { mode: 'overlay', query: { q: highlightQuery } }), [openRecord, highlightQuery]);

  const handleRatingUpdate = useCallback(async (candidateId: string, rating: number) => {
    const toastId = enqueueSnackbar(t('candidates.api.update_rating.loading'), { variant: 'loading', persist: true });
    try {
      await updateRating({ candidateId, rating }).unwrap();
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.success'), { variant: 'success' });
      // Reflect the new rating locally without a full refetch.
      setRows((prev) => prev.map((r) => (r.id === candidateId ? { ...r, rating } : r)));
    } catch {
      closeSnackbar(toastId);
      enqueueSnackbar(t('candidates.api.update_rating.default_error'), { variant: 'error' });
    }
  }, [t, updateRating]);

  const handleAddToJob = useCallback((candidateId: string) => { setCurrentCandidate(candidateId); addToJob.onTrue(); }, [addToJob]);

  const handleInvite = useCallback(async (candidateId: string) => {
    try {
      await createDirectInvite({ candidateId }).unwrap();
      enqueueSnackbar(t('candidates.api.create_direct_invite.success'), { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(generateDirectCandidateInviteMessage(error), { variant: 'error' });
    }
  }, [t, createDirectInvite]);

  const handleSendEmail = useCallback((row: IOrgCandidateListItem) => {
    if (!row.email) return;
    dispatch(openSingleMailerDialog({
      emails: [row.email],
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      templateEntityId: row.id,
      targetType: MailerTargetType.CANDIDATE,
      targetId: row.id,
    }));
  }, [dispatch]);

  const openUnlink = useCallback((id: string) => { setCurrentCandidate(id); unlinkConfirm.onTrue(); }, [unlinkConfirm]);
  const openRemove = useCallback((id: string) => { setCurrentCandidate(id); removeConfirm.onTrue(); }, [removeConfirm]);

  const handleUnlink = useCallback(async () => {
    if (!currentCandidate) return;
    try {
      await unlinkCandidate(currentCandidate).unwrap();
      enqueueSnackbar(t('candidates.api.unlink_candidate.success'), { variant: 'success' });
      setCurrentCandidate(undefined);
      unlinkConfirm.onFalse();
    } catch (error) {
      enqueueSnackbar(generateUnlinkCandidateErrorMessage(error), { variant: 'error' });
    }
  }, [currentCandidate, t, unlinkCandidate, unlinkConfirm]);

  const handleRemove = useCallback(async () => {
    if (!currentCandidate) return;
    try {
      await removeCandidate(currentCandidate).unwrap();
      enqueueSnackbar(t('candidates.api.remove_candidate.success'), { variant: 'success' });
      setCurrentCandidate(undefined);
      removeConfirm.onFalse();
    } catch (error) {
      enqueueSnackbar(generateRemoveCandidateErrorMessage(error), { variant: 'error' });
    }
  }, [currentCandidate, t, removeCandidate, removeConfirm]);

  // Bulk / toolbar actions
  const handleBulkAddToJob = useCallback(() => { if (selectedIds.length) bulkAddToJob.onTrue(); }, [selectedIds, bulkAddToJob]);
  const handleBulkAddToList = useCallback(() => { if (selectedIds.length) addToList.onTrue(); }, [selectedIds, addToList]);
  const onSuccessAddToList = useCallback(() => { setSelectedIds([]); addToList.onFalse(); }, [addToList]);
  const handleBulkEmail = useCallback(() => {
    if (!selectedIds.length) return;
    dispatch(openMultiMailerDialog({
      targetType: MailerTargetType.CANDIDATE,
      templateType: EmailMessageTemplateType.CANDIDATE_ENGAGEMENT,
      entities: selectedIds,
    }));
  }, [dispatch, selectedIds]);

  const handleCreate = useCallback(() => dispatch(toggleCandidateEditorSidesheet({ status: true })), [dispatch]);
  const handleInviteCandidates = useCallback(() => dispatch(toggleCandidateInviteModal({ status: true })), [dispatch]);
  const handleImportFile = useCallback(() => dispatch(toggleCandidateFileImporterModal({ status: true, organizationId: authContext.organization?.id })), [dispatch, authContext.organization?.id]);

  // Columns
  const registry = useMemo(
    () => buildCandidateColumns({ t, tenantType: tenant, onRatingUpdate: handleRatingUpdate, onOpenRecord: handleOpenOverlay, highlights }),
    [t, tenant, handleRatingUpdate, handleOpenOverlay, highlights]
  );
  const { columns, pickerModel, save } = useColumnPreferences('candidates', registry);

  const renderRowLeading = useCallback((row: IOrgCandidateListItem) => (
    <Tooltip title={t('common.quick_view', { defaultValue: 'Quick view' })}>
      <IconButton size="small" onClick={() => handleQuickView(row.id)}>
        <Iconify icon="solar:eye-bold" />
      </IconButton>
    </Tooltip>
  ), [handleQuickView, t]);

  const renderRowActions = useCallback((row: IOrgCandidateListItem) => (
    <CandidateRowActions
      row={row}
      onView={handleQuickView}
      onAddToJob={handleAddToJob}
      onExportProfile={exportOrgCandidateProfile}
      onInvite={handleInvite}
      onSendEmail={handleSendEmail}
      onUnlink={openUnlink}
      onRemove={openRemove}
    />
  ), [handleQuickView, handleAddToJob, handleInvite, handleSendEmail, openUnlink, openRemove]);

  const hasSelection = selectedIds.length > 0;

  const toolbar = (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
      <TextField
        size="small"
        value={searchInput}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('candidates.table.search_placeholder', { defaultValue: 'Search candidates' })}
        InputProps={{ startAdornment: <InputAdornment position="start"><Iconify icon="eva:search-fill" /></InputAdornment> }}
        sx={{ width: 260 }}
      />

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
        <DataListColumnPicker model={pickerModel} onSave={save} />

        <DataListActionsMenu
          actions={[
            { key: 'create', label: t('common.create', { defaultValue: 'Create' }), icon: 'mingcute:add-line', onClick: handleCreate },
            { key: 'invite', label: t('candidates.invite_candidates', { defaultValue: 'Invite' }), icon: 'lucide:mail-plus', onClick: handleInviteCandidates },
            { key: 'import', label: t('common.import', { defaultValue: 'Import' }), icon: 'bx:import', onClick: handleImportFile },
            { key: 'bulk-import', label: t('candidates.bulk_import', { defaultValue: 'Bulk import' }), icon: 'solar:upload-bold', onClick: bulkImport.onTrue },
            { key: 'add-to-job', label: t('common.add_to_job'), icon: 'mdi:invite', onClick: handleBulkAddToJob, disabled: !hasSelection },
            { key: 'add-to-list', label: t('common.add_to_list', { defaultValue: 'Add to list' }), icon: 'solar:list-bold', onClick: handleBulkAddToList, disabled: !hasSelection },
            { key: 'send-email', label: t('common.send_email'), icon: 'lsicon:email-send-filled', onClick: handleBulkEmail, disabled: !hasSelection },
          ]}
        />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Card sx={{ borderRadius: 0 }}>
        <DataList<IOrgCandidateListItem>
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          isSearching={isSearching}
          hasMore={hasMore}
          onLoadMore={onLoadMore}
          sort={sortConfig}
          onSortChange={onSortChange}
          columnSearch={columnSearch}
          onColumnSearchChange={onColumnSearchChange}
          toolbar={toolbar}
          selectable
          selectedIds={selectedIds}
          onSelectRow={onSelectRow}
          onSelectAll={onSelectAll}
          renderRowLeading={renderRowLeading}
          renderRowActions={renderRowActions}
        />
      </Card>

      <RecordPanel type="candidate" ids={rows.map((r) => r.id)} total={count} />

      <AddCandidateJobModal open={addToJob.value} onClose={() => { setCurrentCandidate(undefined); addToJob.onFalse(); }} candidateId={currentCandidate as string} />
      <BulkAddCandidatesJobModal open={bulkAddToJob.value} onClose={bulkAddToJob.onFalse} candidates={selectedIds} />
      <AddItemsModal open={addToList.value} onClose={addToList.onFalse} onSuccess={onSuccessAddToList} items={selectedIds} entityType={ListEntityType.CANDIDATE} />
      <BulkCandidateImportModal open={bulkImport.value} onClose={bulkImport.onFalse} />

      <ConfirmDialog
        open={unlinkConfirm.value}
        onClose={unlinkConfirm.onFalse}
        title={t('candidates.unlink_candidate.title')}
        content={
          <Stack spacing={1}>
            <Typography variant="subtitle2" gutterBottom>{t('candidates.unlink_candidate.confirm_message_1')}</Typography>
            <Typography variant="body2">{t('candidates.unlink_candidate.confirm_message_2')}</Typography>
          </Stack>
        }
        action={<LoadingButton loading={isUnlinking} variant="contained" color="error" onClick={handleUnlink}>{t('common.unlink')}</LoadingButton>}
      />

      <ConfirmDialog
        open={removeConfirm.value}
        onClose={removeConfirm.onFalse}
        title={t('candidates.remove_candidate.title')}
        content={<Typography variant="body2" gutterBottom>{t('candidates.remove_candidate.confirm_message')}</Typography>}
        action={<LoadingButton loading={isRemoving} variant="contained" color="error" onClick={handleRemove}>{t('common.remove')}</LoadingButton>}
      />
    </>
  );
}
